import * as React from "react";

import { cn } from "../../lib/utils";
import { Field, FieldDescription, FieldError, FieldLabel } from "./field";

export interface FormFieldProps {
	label?: React.ReactNode;
	labelFor?: string;
	description?: React.ReactNode;
	hint?: React.ReactNode;
	validationMessage?: React.ReactNode;
	isRequired?: boolean;
	isInvalid?: boolean;
	className?: string;
	children: React.ReactNode;
}

/**
 * A composed field component providing label, description, hint, and validation
 * in a single API. Drop-in replacement for Evergreen's FormField.
 *
 * Emits `data-field` (from `labelFor`) and `data-field-error` so Cypress failure
 * diagnostics can report which field failed validation.
 */
function FormField({
	label,
	labelFor,
	description,
	hint,
	validationMessage,
	isRequired,
	isInvalid,
	className,
	children,
}: FormFieldProps) {
	return (
		<Field
			data-field={labelFor ?? ""}
			data-invalid={isInvalid || undefined}
			className={cn("gap-1.5", className)}
		>
			{label && (
				<FieldLabel htmlFor={labelFor} className="text-left">
					{label}
					{isRequired && (
						<span aria-hidden="true" className="ml-0.5 text-danger-500">
							*
						</span>
					)}
				</FieldLabel>
			)}
			{description && <FieldDescription>{description}</FieldDescription>}
			{children}
			{hint && <FieldDescription>{hint}</FieldDescription>}
			{validationMessage && (
				<FieldError data-field-error="">{validationMessage}</FieldError>
			)}
		</Field>
	);
}

export { FormField };
